import React, {useEffect, useState, useRef} from 'react';
import './club-finder-search-bar.scss';
import SearchSuggestLocation from "../../../molecules/search-suggest-locations/search-suggest-location";
import {clubFinderSearchBarSliceActions} from "../../../../store/slices/clubFinderSearchBarSlice";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../../store/configureStore";
import Drawer from '@mui/material/Drawer';
import FilterContent from "../filter-content/filter-content";
import {clubFinderMapResultSliceActions} from "../../../../store/slices/clubFinderResultMapSlice";
import close from "../../../assets/icons/cross.svg";
import {Config, TenantAppVersionConfig} from "../../../../constants/Config";
import {widgetConfigurationSliceActions} from "../../../../store/slices/widgetConfigurationSlice";

const baseClass = 'v2-clubFinder-search-bar';


interface PropTypes {
  daysFilterOptions: any
  inclusionFilterOptions: any
  genderFilterOptions: any
  formatFilterOptions: any
  tenant: string
}

const MobileViewWidth = Config.breakpoints.large.width;

const ClubFinderSearchBarVersion2 = (props: PropTypes): JSX.Element => {
  const dispatch = useDispatch();
  const tenant = useSelector((state: RootState) => state.widgetConfigurationSettings.tenant);
  const [showMobileSearchBarClass, setShowMobileSearchBarClass] = useState('hide');
  const [drawerWidth, setDrawerWidth] = useState(TenantAppVersionConfig[tenant].filterWidth ? TenantAppVersionConfig[tenant].filterWidth?.large: '35%');
  const [drawerTransitionDirection, setDrawerTransitionDirection] = useState("left");
  const searchButtonPressed = useSelector((state: RootState) => state.clubFinderSearchBarStore.searchButtonPressed);
  const isFilterDrawerOpen = useSelector((state: RootState) => state.clubFinderSearchBarStore.isFilterDrawerVisible);
  const isSelectionSearchable = useSelector((state: RootState) => state.clubFinderSearchBarStore.isSearchable);
  const selectedLocation = useSelector((state: RootState) => state.clubFinderSearchBarStore.selectedLocation);
  const isMobileSearchVisible = useSelector((state: RootState) => state.clubFinderSearchBarStore.isMobileSearchVisible);
  const parentWidth = useSelector((state: RootState) => state.widgetConfigurationSettings.widgetParentWidth);
  const toggleUserLocationFunction = useSelector((state: RootState) => state.clubFinderMapResultStore.toggleUserLocationFunction);
  const applyClickedCounter = useSelector((state: RootState) => state.clubFinderSearchBarStore.applyClickedCounter);
  const selectedFormatFilter = useSelector((state: RootState) => state.clubFinderSearchBarStore.selectedFormat);
  const selectedMultiGenderFilters = useSelector((state: RootState) => state.clubFinderSearchBarStore.selectedMultiGenderFilters);
  const selectedInclusionFilter = useSelector((state: RootState) => state.clubFinderSearchBarStore.selectedInclusionFilters);
  const selectedDaysFilter = useSelector((state: RootState) => state.clubFinderSearchBarStore.selectedDayFilters);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const search = () => {
    try {
      dispatch(clubFinderSearchBarSliceActions.searchButtonPressed(!searchButtonPressed));
      dispatch(clubFinderSearchBarSliceActions.toggleMobileSearch(false));
      closeFilterDrawer();
    } catch (e) {
      console.log("Error finding club:", e);
    }
  }

  const createURLParams = () => {
    const currentURL = new URL(window.location.href);

    if (selectedDaysFilter && selectedDaysFilter.length) {
      currentURL.searchParams.set(Config.filterSearchParams[props.tenant as keyof typeof Config.filterSearchParams].days, selectedDaysFilter.join(","));
    } else if (currentURL.searchParams.has(Config.filterSearchParams[props.tenant as keyof typeof Config.filterSearchParams].days)) {
      currentURL.searchParams.delete(Config.filterSearchParams[props.tenant as keyof typeof Config.filterSearchParams].days);
    }
    if (selectedFormatFilter && selectedFormatFilter.length) {
      currentURL.searchParams.set(Config.filterSearchParams[props.tenant as keyof typeof Config.filterSearchParams].formats, selectedFormatFilter.join(","));
    } else if (currentURL.searchParams.has(Config.filterSearchParams[props.tenant as keyof typeof Config.filterSearchParams].formats)) {
      currentURL.searchParams.delete(Config.filterSearchParams[props.tenant as keyof typeof Config.filterSearchParams].formats);
    }
    if (selectedInclusionFilter && selectedInclusionFilter.length) {
      const inclusionPlayHQValues: string[] = selectedInclusionFilter.map((inclusion: {
        inclusionDisplayValue: string;
        inclusionPlayHQValue: string
      }) => inclusion.inclusionPlayHQValue);
      currentURL.searchParams.set(Config.filterSearchParams[props.tenant as keyof typeof Config.filterSearchParams].inclusions, inclusionPlayHQValues.join(","));
    } else if (currentURL.searchParams.has(Config.filterSearchParams[props.tenant as keyof typeof Config.filterSearchParams].inclusions)) {
      currentURL.searchParams.delete(Config.filterSearchParams[props.tenant as keyof typeof Config.filterSearchParams].inclusions);
    }
    if (selectedMultiGenderFilters && selectedMultiGenderFilters.length) {
      const genderTypes: string[] = selectedMultiGenderFilters.map((gender: {
        type: string;
        name: string;
      }) => gender.type);
      currentURL.searchParams.set(Config.filterSearchParams[props.tenant as keyof typeof Config.filterSearchParams].genders, genderTypes.join(","));
    } else if (currentURL.searchParams.has(Config.filterSearchParams[props.tenant as keyof typeof Config.filterSearchParams].genders)) {
      currentURL.searchParams.delete(Config.filterSearchParams[props.tenant as keyof typeof Config.filterSearchParams].genders);
    }
    window.history.replaceState({}, "", currentURL.href);
  }
  const applyAction = () => {
    try {
      dispatch(clubFinderSearchBarSliceActions.toggleMobileSearch(false));
      closeFilterDrawer();
      dispatch(clubFinderSearchBarSliceActions.setApplyClickedCount(applyClickedCounter + 1));
      createURLParams();
    } catch (e) {
      console.log("Error finding club:", e);
    }
  }

  const hideMobileFilter = () => {
    dispatch(clubFinderMapResultSliceActions.setIsMapExpanded(false));
    dispatch(clubFinderSearchBarSliceActions.toggleMobileSearch(false));
  }

  const openFilterDrawer = (event: any) => {
    try {
      event.preventDefault();
      event.stopPropagation();
      dispatch(clubFinderSearchBarSliceActions.toggleFilterDrawerVisibility(true));
    } catch (e) {
      console.log("Error opening filter:", e);
    }
  }

  const closeFilterDrawer = () => {
    dispatch(clubFinderSearchBarSliceActions.toggleFilterDrawerVisibility(false));
  }

  const handleResetFilter = (clearOrCancel: string) => {
    if (clearOrCancel === 'Clear') {
      dispatch(clubFinderSearchBarSliceActions.setSavedGenderFilter(Config.defaultGenderOption));
      dispatch(clubFinderSearchBarSliceActions.setSavedFormatFilter({
        selectedFormatObject: null,
        selectedFormatTitles: null
      }));
      dispatch(clubFinderSearchBarSliceActions.setSelectedLocation(null));
      dispatch(clubFinderSearchBarSliceActions.activeSearchFilterMobile({
        location: false,
        format: false,
        gender: false
      }));
      dispatch(clubFinderMapResultSliceActions.setClubFinderListResult(null));
    } else {
      hideMobileFilter();
    }
  }

  let clearOrCancel = 'Cancel';
  if (selectedLocation) {
    clearOrCancel = 'Clear'
  }

  useEffect(() => {
    if (isMobileSearchVisible) {
      setShowMobileSearchBarClass('show')
    } else {
      setShowMobileSearchBarClass('hide')
    }
    if (parentWidth <= MobileViewWidth) {
      setDrawerWidth(TenantAppVersionConfig[tenant].filterWidth ? TenantAppVersionConfig[tenant].filterWidth?.small: '100%');
      setDrawerTransitionDirection('left')
    }

    if (MobileViewWidth < parentWidth && parentWidth < Config.breakpoints.extraLarge.width) {
      setDrawerWidth(TenantAppVersionConfig[tenant].filterWidth ? TenantAppVersionConfig[tenant].filterWidth?.medium : '50%');
    }
  }, [isMobileSearchVisible])

  useEffect(() => {
    if(searchBarRef && searchBarRef.current && parentWidth > MobileViewWidth) {
      dispatch(widgetConfigurationSliceActions.setFloatingBarWidth(searchBarRef.current.getBoundingClientRect().width));
    }
  });


  const getUserCurrentLocation = (event: any) => {
    event.stopPropagation();
    event.preventDefault();
    dispatch(clubFinderMapResultSliceActions.callGetUserLocationFunction(!toggleUserLocationFunction));
  }

  return (
    <div ref={searchBarRef} className={`${baseClass} ${tenant} ${baseClass}--mobile-${showMobileSearchBarClass} ${selectedLocation ? baseClass+"--location-selected" : baseClass+"--location-not-selected"}`}>
      <div className={`${baseClass}--mobile-show-header`}>
        <div className={`${baseClass}--hide-search-button`} onClick={hideMobileFilter}>
          <img src={close} alt={'Close'}/>
        </div>
        <div className={`${baseClass}--search-filter ${baseClass}--search-filter-location`}>
          <SearchSuggestLocation
            search={search}
            searchButtonPressed={searchButtonPressed}
            openFilterDrawer={(event: any) => openFilterDrawer(event)}
            getUserCurrentLocation={getUserCurrentLocation}
          />
        </div>
      </div>
      <div className={`${baseClass}--footer`}>
        <div className={`${baseClass}--footer-clear-button`} onClick={() => handleResetFilter(clearOrCancel)}>
          {clearOrCancel}
        </div>
        <div
          className={`${baseClass}--footer-search-button ${isSelectionSearchable ? 'searchable' : 'not-searchable'}`}
          onClick={isSelectionSearchable ? search : () => void (0)}>Search
        </div>
      </div>
      <Drawer
        open={isFilterDrawerOpen}
        onClose={closeFilterDrawer}
        PaperProps={{
          sx: {
            width: drawerWidth
          }
        }}
        // @ts-ignore
        anchor={drawerTransitionDirection}
      >
        <FilterContent daysFilterOptions={props.daysFilterOptions} genderFilterOptions={props.genderFilterOptions}
                       inclusionFilterOptions={props.inclusionFilterOptions}
                       tenant={props.tenant}
                       formatFilterOptions={props.formatFilterOptions} handleBackAction={closeFilterDrawer}
                       handleApplyAction={applyAction}/>
      </Drawer>
    </div>
  )
}

export default ClubFinderSearchBarVersion2;
